import type { FilterConfig } from '#src/components/filter/Filter.tsx'

export type FilterOperator =
  | 'equals'
  | 'not-equals'
  | 'in'
  | 'not-in'
  | 'contains'
  | 'not-contains'
  | 'before'
  | 'after'
  | 'between'
  | 'is_today'
  | 'is_yesterday'
  | 'is_tomorrow'
  | 'is_past'
  | 'is_future'
  | 'is_this_week'
  | 'is_last_week'
  | 'is_next_week'
  | 'is_this_month'
  | 'is_last_month'
  | 'is_next_month'
  | 'in_last_days'
  | 'in_next_days'

export type FilterOperatorConfig = {
  value: FilterOperator
  label: string
  symbol: string
}

export const FILTER_OPERATORS: Record<
  FilterConfig<any>['type'] | 'date' | 'boolean',
  FilterOperatorConfig[]
> = {
  'multi-select': [
    { value: 'in', label: 'Is', symbol: 'is' },
    { value: 'not-in', label: 'Is not', symbol: 'is not' },
  ],
  'multi-select-array': [
    { value: 'in', label: 'include all of', symbol: 'include all of' },
    { value: 'contains', label: 'include any of', symbol: 'include any of' },
    {
      value: 'not-in',
      label: 'exclude if any of',
      symbol: 'exclude if any of',
    },
    {
      value: 'not-contains',
      label: 'exclude if all of',
      symbol: 'exclude if all of',
    },
  ],
  text: [
    { value: 'contains', label: 'Contains', symbol: 'contains' },
    {
      value: 'not-contains',
      label: 'Does not contain',
      symbol: 'does not contain',
    },
  ],
  date: [
    { value: 'equals', label: 'Is', symbol: 'is' },
    { value: 'not-equals', label: 'Is not', symbol: 'is not' },
    { value: 'before', label: 'Before', symbol: 'before' },
    { value: 'after', label: 'After', symbol: 'after' },
    { value: 'between', label: 'Between', symbol: 'between' },
    { value: 'is_today', label: 'Is today', symbol: 'is today' },
    { value: 'is_yesterday', label: 'Is yesterday', symbol: 'is yesterday' },
    { value: 'is_tomorrow', label: 'Is tomorrow', symbol: 'is tomorrow' },
    { value: 'is_past', label: 'Is in the past', symbol: 'is past' },
    { value: 'is_future', label: 'Is in the future', symbol: 'is future' },
    { value: 'is_this_week', label: 'Is this week', symbol: 'this week' },
    { value: 'is_last_week', label: 'Is last week', symbol: 'last week' },
    { value: 'is_next_week', label: 'Is next week', symbol: 'next week' },
    { value: 'is_this_month', label: 'Is this month', symbol: 'this month' },
    { value: 'is_last_month', label: 'Is last month', symbol: 'last month' },
    { value: 'is_next_month', label: 'Is next month', symbol: 'next month' },
    {
      value: 'in_last_days',
      label: 'Within last X days',
      symbol: 'in last X days',
    },
    {
      value: 'in_next_days',
      label: 'Within next X days',
      symbol: 'in next X days',
    },
  ],
  boolean: [{ value: 'equals', label: 'Is', symbol: 'is' }],
}

const SIMPLE_RELATIVE_OPERATORS: FilterOperator[] = [
  'is_today',
  'is_yesterday',
  'is_tomorrow',
  'is_past',
  'is_future',
  'is_this_week',
  'is_last_week',
  'is_next_week',
  'is_this_month',
  'is_last_month',
  'is_next_month',
]

const PARAMETERIZED_RELATIVE_OPERATORS: FilterOperator[] = [
  'in_last_days',
  'in_next_days',
]

export type DateOperatorCategory =
  | 'simple-relative'
  | 'parameterized-relative'
  | 'between'
  | 'absolute'

export function getDateOperatorCategory(
  operator: FilterOperator | undefined
): DateOperatorCategory {
  if (!operator || operator === 'equals') {
    return 'absolute'
  }
  if (SIMPLE_RELATIVE_OPERATORS.includes(operator)) {
    return 'simple-relative'
  }
  if (PARAMETERIZED_RELATIVE_OPERATORS.includes(operator)) {
    return 'parameterized-relative'
  }
  if (operator === 'between') {
    return 'between'
  }
  return 'absolute'
}
